// Centralised, type-safe SEO helper.
//
// Emits a correct, deduped set of <title>, <meta>, canonical and JSON-LD tags
// for a page. Using `useSeoMeta` (rather than raw `meta` arrays) guarantees the
// Open Graph tags are rendered as `property="og:*"` and the Twitter tags as
// `name="twitter:*"`, which is what social/scraper crawlers actually read.

const SITE_URL = 'https://vortec.io';
const DEFAULT_IMAGE = `${SITE_URL}/vortecShare.png`;

type JsonLd = Record<string, unknown>;

interface SeoInput {
  /** Full <title>, e.g. "About | VORTEC". */
  title: string;
  /** Meta description (~150-160 chars ideal). */
  description: string;
  /** Absolute path of the page, e.g. "/about". Used for canonical + og:url. */
  path: string;
  /** Share image. Absolute URL or site-relative path. Defaults to the brand share image. */
  image?: string;
  /** Open Graph type. Defaults to "website". */
  type?: 'website' | 'article' | 'profile';
  /** Keep the page out of search indexes (e.g. transactional pages). */
  noindex?: boolean;
  /** Schema.org JSON-LD. "@context" is added automatically. Accepts one block or many. */
  jsonLd?: JsonLd | JsonLd[];
}

export function useSeo(input: SeoInput) {
  // Keep the homepage canonical free of a trailing slash ("/" -> "").
  const path = input.path === '/' ? '' : input.path;
  const url = `${SITE_URL}${path}`;
  const image = input.image
    ? input.image.startsWith('http')
      ? input.image
      : `${SITE_URL}${input.image}`
    : DEFAULT_IMAGE;

  useSeoMeta({
    title: input.title,
    description: input.description,
    ogTitle: input.title,
    ogDescription: input.description,
    ogType: input.type ?? 'website',
    ogUrl: url,
    ogImage: image,
    ogImageAlt: input.title,
    twitterCard: 'summary_large_image',
    twitterTitle: input.title,
    twitterDescription: input.description,
    twitterImage: image,
    ...(input.noindex ? { robots: 'noindex, nofollow' } : {}),
  });

  const head: Parameters<typeof useHead>[0] = {
    link: [{ rel: 'canonical', href: url }],
  };

  if (input.jsonLd) {
    const blocks = Array.isArray(input.jsonLd) ? input.jsonLd : [input.jsonLd];
    head.script = blocks.map((block) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify({ '@context': 'https://schema.org', ...block }),
    }));
  }

  useHead(head);
}
